import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { Pagination } from "@/domain/entities/Pagination";
import {
  HandoverList,
  HandoverDetail,
  PartnerVendorList,
  FailedVendorStt,
  HandoverFailedStt,
  RetryHandoverVendorResult,
  HandoverFailedVendorSttResponse
} from "@/domain/entities/Handover";
import { HandoverPresenter } from "@/app/ui/presenters/HandoverPresenter";
import { SubconsoleEntities } from "@/domain/entities/Subconsole";
import { container } from "tsyringe";
import {
  ResponsePayload,
  ResponseSttDetail
} from "@/domain/entities/ResponsePayload";
import {
  RequestExportFileExcel,
  RequestFailedVendorStt,
  RequestListHandover,
  RequestPartnerVendorList,
  RetryHandoverVendorRequest,
  UpdateApiRequest
} from "@/data/payload/api/HandoverApiRequest";
import { MainAppController } from "./MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";

export interface HandoverState {
  isLoading: boolean;
  isError: boolean;
  errorCause: string;
  handoverList: HandoverList;
  subconsoleList: SubconsoleEntities;
}

@Module({ namespaced: true, dynamic: true, store, name: "handover" })
class HandoverStore extends VuexModule implements HandoverState {
  public isLoading = false;
  public isLoadingDetail = false;
  public isLoadingSttDetail = false;
  public isLoadingSubConsole = false;
  public isError = false;
  public errorCause = "";
  public filter = new RequestListHandover();
  public handoverList = new HandoverList(new Pagination(1, 10), []);
  public subconsoleList = new SubconsoleEntities(new Pagination(1, 10), []);
  public detailHandover = new HandoverDetail();
  public isLoadingPartnerVendor = false;
  public partnerVendorList: Array<PartnerVendorList> = [];
  public isLoadingFailedVendorStt = false;
  public failedVendorSttByHandoverId: Record<number, FailedVendorStt> = {};
  public isRetrying = false;
  public isLoadingHandoverFailedStt = false;
  public handoverFailedSttByHandoverId: Record<number, HandoverFailedStt> = {};
  public failedVendorStt = new HandoverFailedVendorSttResponse();

  @Action
  public getHandoverList(params: RequestListHandover) {
    this.setIsLoading(true);
    const presenter = container.resolve(HandoverPresenter);
    presenter
      .getHandoverList(params)
      .then((res: HandoverList) => {
        this.setHandoverList(res);
        this.setErrorCause("");
      })
      .catch((err: any) => {
        this.setErrorCause(err.response ? "server" : "internet");
        this.setHandoverList(new HandoverList(new Pagination(0, 0), []));
      })
      .finally(() => {
        this.setIsLoading(false);
      });
  }

  @Action
  public fetchHandoverList() {
    this.getHandoverList(
      new RequestListHandover({
        page: this.handoverList.pagination.page,
        limit: this.handoverList.pagination.limit,
        startDate: new Date(this.filter.startDate).toLocaleDateString("fr-CA"),
        endDate: new Date(this.filter.endDate).toLocaleDateString("fr-CA"),
        destinationPartnerId: this.filter.destinationPartnerId,
        search: this.filter.search,
        sortBy: this.filter.sortBy,
        orderBy: this.filter.orderBy
      })
    );
  }

  @Action
  public getDetailHandover(handoverId: number) {
    this.setIsLoadingDetail(true);
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .getDetailHandover(handoverId)
      .then((res: HandoverDetail) => {
        this.setError(false);
        this.setErrorCause("");
        this.setDetailHandover(res);
        return true;
      })
      .catch((err: any) => {
        this.setError(true);
        this.setErrorCause(err.response ? "server" : "internet");
        return false;
      })
      .finally(() => {
        this.setIsLoadingDetail(false);
      });
  }

  @Action
  public getListSubconsole(params: { parentId: number }) {
    this.setIsLoadingSubConsole(true);
    const presenter = container.resolve(HandoverPresenter);
    presenter
      .getListSubconsole(params.parentId)
      .then((res: SubconsoleEntities) => {
        this.setSubconsoleList(res);
      })
      .catch((error: any) => {
        console.log(error);
      })
      .finally(() => {
        this.setIsLoadingSubConsole(false);
      });
  }

  @Action
  public getPartnerVendor(params: RequestPartnerVendorList) {
    this.setLoadingPartnerVendor(true);
    this.setPartnerVendorList([]);
    const presenter = container.resolve(HandoverPresenter);
    presenter
      .getPartnerVendorList(params)
      .then((res: PartnerVendorList[]) => this.setPartnerVendorList(res))
      .finally(() => this.setLoadingPartnerVendor(false));
  }

  @Action
  public getSttDetail(params: {
    stt: string;
    callback: any;
    bagNo: string;
    partnerCode: string;
    noRef?: string;
  }) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .getSttDetail(params.stt, params.bagNo, params.partnerCode)
      .then((res: ResponseSttDetail) => {
        params.callback(res, params.noRef);
        return res;
      })
      .catch((err: any) => {
        throw err;
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public onUpdate(params: UpdateApiRequest) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .updateHandover(params)
      .then((res: ResponsePayload) => {
        return res.data;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Perubahan Status Gagal!", () =>
            this.onUpdate(params)
          )
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public exportExcelFile(params: RequestExportFileExcel) {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .getExcelFile(params)
      .then((res: any) => {
        return res;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Export Excel Gagal!", () =>
            this.exportExcelFile(params)
          )
        );
      })
      .finally(() => {
        MainAppController.closeLoading();
      });
  }

  @Action
  public getHandoverReceipt(id: number) {
    const presenter = container.resolve(HandoverPresenter);
    return presenter.getHandoverReceipt(id);
  }

  @Action
  public getFailedVendorStt(
    payload:
      | number
      | {
          handoverId: number;
          params?: RequestFailedVendorStt;
          append?: boolean;
        }
  ): Promise<FailedVendorStt> {
    const handoverId =
      typeof payload === "number" ? payload : payload.handoverId;
    const params = typeof payload === "number" ? undefined : payload.params;
    const append =
      typeof payload === "number" ? false : Boolean(payload.append);

    this.setLoadingFailedVendorStt(true);
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .getFailedVendorStt(handoverId, params)
      .then((res: FailedVendorStt) => {
        this.setFailedVendorStt({ handoverId, data: res, append });
        return res;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Gagal memuat daftar STT!", () =>
            this.getFailedVendorStt(payload)
          )
        );
        throw err;
      })
      .finally(() => {
        this.setLoadingFailedVendorStt(false);
      });
  }

  @Action
  public async retryHandoverVendorOrder(params: {
    handoverId: number;
    sttNo?: string;
    sttNos?: string[];
  }): Promise<RetryHandoverVendorResult> {
    this.setIsRetrying(true);
    const presenter = container.resolve(HandoverPresenter);
    try {
      const stts =
        params.sttNos && params.sttNos.length
          ? params.sttNos
          : params.sttNo
          ? [params.sttNo]
          : [];

      const payload =
        stts.length > 1
          ? new RetryHandoverVendorRequest({ sttNos: stts })
          : new RetryHandoverVendorRequest({
              sttNo: stts[0] || params.sttNo || ""
            });

      const res = await presenter.retryHandoverVendorOrder(
        params.handoverId,
        payload
      );

      const targetHandoverId = res.handoverId || params.handoverId;
      const returnedStatus = res.vendorOrderStatus || "IN_PROGRESS";
      this.updateVendorOrderStatus({
        handoverId: targetHandoverId,
        vendorOrderStatus: returnedStatus
      });

      return res;
    } finally {
      this.setIsRetrying(false);
    }
  }

  @Action
  public getHandoverFailedStt(handoverId: number): Promise<HandoverFailedStt> {
    this.setLoadingHandoverFailedStt(true);
    const presenter = container.resolve(HandoverPresenter);
    return presenter
      .getHandoverFailedStt(handoverId)
      .then((res: HandoverFailedStt) => {
        this.setHandoverFailedStt({ handoverId, data: res });
        return res;
      })
      .catch((err: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(err, "Gagal memuat daftar STT!", () =>
            this.getHandoverFailedStt(handoverId)
          )
        );
        throw err;
      })
      .finally(() => {
        this.setLoadingHandoverFailedStt(false);
      });
  }

  @Mutation
  private setHandoverList(data: HandoverList) {
    this.handoverList = data;
  }

  @Mutation
  private setPartnerVendorList(data: PartnerVendorList[]) {
    this.partnerVendorList = data;
  }

  @Mutation
  public setSubConsoleId(subConsoleId: string) {
    this.filter.destinationPartnerId = subConsoleId;
  }

  @Mutation
  public setSearch(id: string) {
    this.handoverList.pagination.page = 1;
    this.filter = new RequestListHandover({
      ...this.filter,
      search: id,
      sortBy: id ? "handover_id" : "",
      orderBy: id ? "ASC" : ""
    });
  }

  @Mutation
  private setSubconsoleList(data: SubconsoleEntities) {
    this.subconsoleList = data;
  }

  @Mutation
  private setDetailHandover(data: HandoverDetail) {
    this.detailHandover = data;
  }

  @Mutation
  private setIsLoadingFailedVendorStt(value: boolean) {
    this.isLoadingFailedVendorStt = value;
  }

  @Mutation
  public setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @Mutation
  public setLoadingSttDetail(value: boolean) {
    this.isLoadingSttDetail = value;
  }

  @Mutation
  public setIsLoadingDetail(value: boolean) {
    this.isLoadingDetail = value;
  }

  @Mutation
  public setIsLoadingSubConsole(value: boolean) {
    this.isLoadingSubConsole = value;
  }

  @Mutation
  public setLoadingPartnerVendor(value: boolean) {
    this.isLoadingPartnerVendor = value;
  }

  @Mutation
  public setLoadingFailedVendorStt(value: boolean) {
    this.isLoadingFailedVendorStt = value;
  }

  @Mutation
  public setIsRetrying(value: boolean) {
    this.isRetrying = value;
  }

  @Mutation
  public setLoadingHandoverFailedStt(value: boolean) {
    this.isLoadingHandoverFailedStt = value;
  }

  @Mutation
  public setHandoverFailedStt(payload: {
    handoverId: number;
    data: HandoverFailedStt;
  }) {
    this.handoverFailedSttByHandoverId = {
      ...this.handoverFailedSttByHandoverId,
      [payload.handoverId]: payload.data
    };
  }

  @Mutation
  public setFailedVendorStt(payload: {
    handoverId: number;
    data: FailedVendorStt;
    append?: boolean;
  }) {
    if (
      payload.append &&
      this.failedVendorSttByHandoverId[payload.handoverId]
    ) {
      const existing = this.failedVendorSttByHandoverId[payload.handoverId];
      const existingSttNos = new Set(existing.stt.map((s) => s.sttNo));
      const newItems = payload.data.stt.filter(
        (s) => !existingSttNos.has(s.sttNo)
      );
      this.failedVendorSttByHandoverId = {
        ...this.failedVendorSttByHandoverId,
        [payload.handoverId]: new FailedVendorStt({
          ...payload.data,
          stt: [...existing.stt, ...newItems]
        })
      };
    } else {
      this.failedVendorSttByHandoverId = {
        ...this.failedVendorSttByHandoverId,
        [payload.handoverId]: payload.data
      };
    }

    const data = this.failedVendorSttByHandoverId[payload.handoverId];
    this.failedVendorStt = new HandoverFailedVendorSttResponse({
      vendorOrderStatus: data.vendorOrderStatus,
      isViewDetailVendorOrder: data.isViewDetailVendorOrder,
      isRetryAvailable: data.isRetryAvailable,
      stt: data.stt.map((item) => ({
        sttNo: item.sttNo,
        status: item.status,
        reason: item.reason,
        reasonEn: item.reasonEn,
        message: item.message,
        retryAvailable: item.retryAvailable
      }))
    });
  }

  @Mutation
  public clearFailedVendorStt(handoverId?: number) {
    if (handoverId != null) {
      delete this.failedVendorSttByHandoverId[handoverId];
    } else {
      this.failedVendorSttByHandoverId = {};
    }
  }

  @Mutation
  public updateVendorOrderStatus(payload: {
    handoverId: number;
    vendorOrderStatus: string;
  }) {
    const list = this.handoverList.data.map((item) => {
      if (Number(item.handoverId) === Number(payload.handoverId)) {
        item.vendorOrderStatus = payload.vendorOrderStatus;
        if (payload.vendorOrderStatus === "IN_PROGRESS") {
          item.isRetryAvailable = false;
        }
      }
      return item;
    });
    this.handoverList = new HandoverList(this.handoverList.pagination, list);

    if (this.failedVendorSttByHandoverId[payload.handoverId]) {
      this.failedVendorSttByHandoverId[payload.handoverId].vendorOrderStatus =
        payload.vendorOrderStatus;
      if (payload.vendorOrderStatus === "IN_PROGRESS") {
        this.failedVendorSttByHandoverId[
          payload.handoverId
        ].isRetryAvailable = false;
      }
    }
  }

  @Mutation
  public setFilter(params: RequestListHandover) {
    this.filter = params;
  }

  @Mutation
  public setError(value: boolean) {
    this.isError = value;
  }

  @Mutation
  public setErrorCause(value: string) {
    this.errorCause = value;
  }
}

export const HandoverController = getModule(HandoverStore);
